import { useState, useEffect, useCallback, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiGetTmContacts, apiImportTmContacts, type TmContactPreview } from '../api';
import PageHeader from '../components/PageHeader';

// Review screen: list TM contacts, pick/create a role per contact, then import.
export default function ContactImportReviewPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<TmContactPreview[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ created: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGetTmContacts()
      .then((r) => {
        if (cancelled) return;
        setItems(r.contacts);
        setRoles(r.existingRoles);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) { setError(e.message || 'Failed to load TM contacts'); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, []);

  const setRole = useCallback((idx: number, role: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, role } : it)));
    if (role && !roles.includes(role)) setRoles((r) => [...r, role]);
  }, [roles]);

  const setFunc = useCallback((idx: number, func: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, function: func } : it)));
  }, []);

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    try {
      const payload = items.map((it) => ({
        name: it.name,
        lastName: it.lastName || '',
        email: it.email || '',
        phone: it.phone || '',
        company: it.company || '',
        role: it.role || '',
        function: it.function || '',
        photo: it.photo || '',
        tmVenueId: it.tmVenueId ?? null,
      }));
      const res = await apiImportTmContacts(payload);
      setResult({ created: res.created });
    } catch (e: any) {
      setError(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="page" style={{ padding: 48, textAlign: 'center' }}><p style={{ color: 'var(--text-muted)' }}>{t('tmImport.loadingContacts')}</p></div>;

  return (
    <div ref={denseRef} className="page musician-edit-page" data-density="socle">
      <PageHeader
        title={t('tmImport.contactsTitle')}
        actions={<button className="btn btn-secondary" onClick={() => navigate('/tour/contacts')}>{t('tmImport.back')}</button>}
      />

      {error && <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}
      {result && <div style={{ color: 'var(--success)', marginBottom: 12 }}>✅ {t('tmImport.importedContacts', { count: result.created })} <button className="btn btn-link" onClick={() => navigate('/tour/contacts')}>{t('tmImport.viewContacts')}</button></div>}

      {items.length === 0 && !result && <p style={{ color: 'var(--text-muted)' }}>{t('tmImport.noContacts')}</p>}

      <div className="table-wrapper">
        <table className="tbl-dense" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>
              <th style={{ padding: 8 }}>{t('tmImport.colName')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colEmail')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colCompany')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colRole')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colFunction')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colDob')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
                <td style={{ padding: 8 }}>
                  {it.photo ? <img src={it.photo} alt="" width={28} height={28} style={{ borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }} /> : null}
                  <span style={{ verticalAlign: 'middle' }}>{it.name}{it.lastName ? ' ' + it.lastName : ''}</span>
                </td>
                <td style={{ padding: 8 }}>{it.email || '—'}</td>
                <td style={{ padding: 8 }}>{it.company || '—'}</td>
                <td style={{ padding: 8 }}>
                  <input
                    list="existing-roles"
                    type="text"
                    value={it.role || ''}
                    onChange={(e) => setRole(i, e.target.value)}
                    placeholder={t('tmImport.rolePh')}
                    style={{ width: 140 }}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input
                    type="text"
                    value={it.function || ''}
                    onChange={(e) => setFunc(i, e.target.value)}
                    placeholder={t('tmImport.functionPh')}
                    style={{ width: 160 }}
                  />
                </td>
                <td style={{ padding: 8 }}>{it.dob || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <datalist id="existing-roles">
        {roles.map((r) => <option key={r} value={r} />)}
      </datalist>

      {items.length > 0 && !result && (
        <div className="form-actions" style={{ marginTop: 20 }}>
          <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
            {importing ? t('tmImport.importing') : t('tmImport.importContacts', { count: items.length })}
          </button>
        </div>
      )}
    </div>
  );
}